/**
 * usePayrollCalculations.ts - 급여 계산 로직 훅 (FR-13)
 *
 * payroll-management.tsx에서 추출된 계산 함수 모음
 * Zero behavior change: 기존 로직을 그대로 보존
 */

import { useState, useCallback, useMemo, useRef } from 'react';
import type { Employee, BudgetData, TeamIncentiveCalculation } from '../../contexts/AppContext.tsx';
import {
  calculateSalaryTotal,
  calculatePureTargetSales,
  calculateDirectorCostAllocation,
  calculateTeamSalaryTotal,
  resolveTeamIncentiveRatio,
  getTeamAdjustmentCarryover,
  resolveFinalTargetSales,
  isPartPosition,
} from '../../utils/payrollCalculations.ts';
import { getSafeMonthlyData } from '../../utils/payrollDataUtils.ts';
import { defaultTaxTable } from '../../data/taxTable.ts';
import type { SortType } from '../../types/payroll.ts';


// ═══════════════════════════════════════
// Debug Utilities (module-level)
// ═══════════════════════════════════════

const IS_DEVELOPMENT = process.env.NODE_ENV === 'development';
const DEBUG_TARGET_SALES_CALCULATION = IS_DEVELOPMENT;
const DEBUG_PREFIX = '[🎯 순수목표매출 계산]';

const debugLog = (message: string, data?: any, forceLog: boolean = false) => {
  const isDebugEnabled = IS_DEVELOPMENT && ((window as any).DEBUG_TARGET_SALES_CALCULATION ?? DEBUG_TARGET_SALES_CALCULATION);
  if (!isDebugEnabled && !forceLog) return;

  if (data) {
  } else {
  }
};

const debugGroup = (title: string, callback: () => void) => {
  const isDebugEnabled = IS_DEVELOPMENT && ((window as any).DEBUG_TARGET_SALES_CALCULATION ?? DEBUG_TARGET_SALES_CALCULATION);
  if (!isDebugEnabled) {
    callback();
    return;
  }

  console.group(`${DEBUG_PREFIX} ${title}`);
  callback();
  console.groupEnd();
};

const debugCalculationStep = (step: string, input: any, output: any, formula?: string) => {
  const isDebugEnabled = IS_DEVELOPMENT && ((window as any).DEBUG_TARGET_SALES_CALCULATION ?? DEBUG_TARGET_SALES_CALCULATION);
  if (!isDebugEnabled) return;
};

// ═══════════════════════════════════════
// Pure Calculation Functions (module-level)
// ═══════════════════════════════════════

/**
 * 소득세 계산 (간이세액표 기준)
 */
export const calculateIncomeTax = (taxableIncome: number, adjustedFamilyMembers: number): number => {
  const monthlyIncomeInThousands = Math.round(taxableIncome / 1000);
  const familyIndex = Math.min(adjustedFamilyMembers - 1, 10);
  let incomeTax = 0;

    const over10mData = defaultTaxTable.find(item => item.min === 10000);

  if (monthlyIncomeInThousands >= 10000) {
    if (over10mData && over10mData.baseAmounts) {
      const baseAmount = over10mData.baseAmounts[familyIndex] || 0;
      if (monthlyIncomeInThousands <= 14000) incomeTax = baseAmount + ((monthlyIncomeInThousands - 10000) * 0.98 * 0.35);
      else if (monthlyIncomeInThousands <= 28000) incomeTax = baseAmount + 1372000 + ((monthlyIncomeInThousands - 14000) * 0.98 * 0.38);
      else if (monthlyIncomeInThousands <= 30000) incomeTax = baseAmount + 6585600 + ((monthlyIncomeInThousands - 28000) * 0.98 * 0.40);
      else if (monthlyIncomeInThousands <= 45000) incomeTax = baseAmount + 7369600 + ((monthlyIncomeInThousands - 30000) * 0.40);
      else if (monthlyIncomeInThousands <= 87000) incomeTax = baseAmount + 13369600 + ((monthlyIncomeInThousands - 45000) * 0.42);
      else incomeTax = baseAmount + 31009600 + ((monthlyIncomeInThousands - 87000) * 0.45);
    }
  } else {
    let exactMatch = false;
    for (const bracket of defaultTaxTable) {
      if (bracket.min === 10000) continue;
      if (monthlyIncomeInThousands >= bracket.min && monthlyIncomeInThousands < bracket.max) {
        if (bracket.taxes && bracket.taxes.length > familyIndex) {
          incomeTax = bracket.taxes[familyIndex] || 0;
          exactMatch = true;
          break;
        }
      }
    }
    if (!exactMatch) {
      let closestLowerBracket: any = null;
      let closestUpperBracket: any = null;
      for (const bracket of defaultTaxTable) {
        if (bracket.min === 10000) continue;
        if (bracket.min <= monthlyIncomeInThousands) {
              if (!closestLowerBracket || bracket.min > closestLowerBracket.min) {
            closestLowerBracket = bracket;
          }
          }
          if (bracket.min > monthlyIncomeInThousands) {
              if (!closestUpperBracket || bracket.min < closestUpperBracket.min) {
            closestUpperBracket = bracket;
          }
        }
      }
      if (closestLowerBracket && closestUpperBracket && closestLowerBracket.taxes && closestUpperBracket.taxes) {
        const lowerTax = closestLowerBracket.taxes[familyIndex] || 0;
        const upperTax = closestUpperBracket.taxes[familyIndex] || 0;
          const ratio = (monthlyIncomeInThousands - closestLowerBracket.min) / (closestUpperBracket.min - closestLowerBracket.min);
          if (isFinite(ratio)) {
        incomeTax = Math.round(lowerTax + ratio * (upperTax - lowerTax));
          } else if (closestLowerBracket.min === monthlyIncomeInThousands) {
               incomeTax = lowerTax;
          } else {
               incomeTax = lowerTax; // Fallback if upper and lower are the same or ratio is not finite
          }
      } else if (closestLowerBracket && closestLowerBracket.taxes) {
        incomeTax = closestLowerBracket.taxes[familyIndex] || 0;
      } else if (closestUpperBracket && closestUpperBracket.taxes) {
        incomeTax = closestUpperBracket.taxes[familyIndex] || 0;
      }
    }
  }
  return Math.max(0, Math.round(incomeTax / 10) * 10);
};

/**
 * 인센티브 산출 매출 계산 (팀별 초과매출/차감분 산출)
 */
export const calculateIncentiveCalculationSales = (
  teams: string[],
  teamActualSales: Record<string, number>,
  teamTargetSales: Record<string, number>,
  teamSalaryTotals: Record<string, number>,
  deductionRate: number = 50
): Record<string, TeamIncentiveCalculation> => {

  const calculations: Record<string, TeamIncentiveCalculation> = {};

  // 1단계: 팀별 초과매출/부족분 계산
  let totalExceededSales = 0;
  let totalUnderperformance = 0;
  const teamExceededSales: Record<string, number> = {};
  const teamUnderperformance: Record<string, number> = {};

  teams.forEach(teamName => {
    const actualSales = teamActualSales[teamName] || 0;
    const targetSales = teamTargetSales[teamName] || 0;
    const difference = actualSales - targetSales;

    if (difference > 0) {
      teamExceededSales[teamName] = difference;
      totalExceededSales += difference;
    } else if (difference < 0) {
      teamUnderperformance[teamName] = Math.abs(difference);
      totalUnderperformance += Math.abs(difference);
    } else {
    }
  });

  // 2단계: 차감분 계산 (미달성분의 설정된 비율)
  const totalDeduction = totalUnderperformance * (deductionRate / 100);

  // 3단계: 팀별 계산 결과 생성
  teams.forEach(teamName => {
    const actualSales = teamActualSales[teamName] || 0;
    const targetSales = teamTargetSales[teamName] || 0;
    const exceededSales = Math.max(0, actualSales - targetSales);

    let deductionAmount = 0;

    // 🔧 중요: 초과달성팀에만 차감분 배분 (미달성팀은 차감분 0)
    if (exceededSales > 0 && totalExceededSales > 0 && totalDeduction > 0) {
      const deductionRatio = exceededSales / totalExceededSales;
      deductionAmount = totalDeduction * deductionRatio;
    } else {
    }

    const incentiveCalculationSales = Math.max(0, exceededSales - deductionAmount);

    calculations[teamName] = {
      exceededSales,
      deductionAmount,
      incentiveCalculationSales
    };

  });

  return calculations;
};

// ═══════════════════════════════════════
// Hook Interface
// ═══════════════════════════════════════

export interface UsePayrollCalculationsParams {
  // Core data
  employees: Employee[];
  budgetData: BudgetData;
  selectedMonth: string;
  localTeams: string[];
  teamActualSales: Record<string, number>;

  // Settings (from PayrollSettingsContext)
  underperformanceDeductionRate: number;
  applyPreviousMonthAdjustment: boolean;
  previousMonthData: any;
  adjustmentAppliedTeams: Set<string>;

  // Loading state (from PayrollLoadingContext)
  executingTeams: Set<string>;

  // Sort configuration
  teamSortType: SortType;

  // State setters
  setBudgetData: React.Dispatch<React.SetStateAction<BudgetData>>;
  setExecutingTeams: React.Dispatch<React.SetStateAction<Set<string>>>;
  setAdjustmentAppliedTeams: React.Dispatch<React.SetStateAction<Set<string>>>;
  setUnderperformanceDeductionRate: React.Dispatch<React.SetStateAction<number>>;
  setIsDataChanged: React.Dispatch<React.SetStateAction<boolean>>;

  // Helper functions from parent
  getSortedTeams: (teams: string[], sortType: SortType) => string[];
  addChangeLog: (message: string) => void;
}

// ═══════════════════════════════════════
// Hook: usePayrollCalculations
// ═══════════════════════════════════════

export function usePayrollCalculations(params: UsePayrollCalculationsParams) {
  const {
    employees,
    budgetData,
    selectedMonth,
    localTeams,
    teamActualSales,
    underperformanceDeductionRate,
    applyPreviousMonthAdjustment,
    previousMonthData,
    adjustmentAppliedTeams,
    executingTeams,
    teamSortType,
    setBudgetData,
    setExecutingTeams,
    setAdjustmentAppliedTeams,
    setUnderperformanceDeductionRate,
    setIsDataChanged,
    getSortedTeams,
    addChangeLog,
  } = params;

  // ── Internal refs ──
  const previousLogValuesRef = useRef<Map<string, any>>(new Map());

  // ── logIfChanged callback ──
  const logIfChanged = useCallback((key: string, data: any, logFunction: () => void) => {
    const currentValue = JSON.stringify(data);
    const previousValue = previousLogValuesRef.current.get(key);

    if (currentValue !== previousValue) {
      previousLogValuesRef.current.set(key, currentValue);
      logFunction();
    }
  }, []);

  // ── teamAdjustmentCache (useMemo) ──
  const teamAdjustmentCache = useMemo(() => {
    const cache = new Map<string, { canApplyAdjustment: boolean; previousUnderperformance: number; lastLoggedValues?: any }>();

    if (!applyPreviousMonthAdjustment || !previousMonthData) {
      return cache;
    }

    localTeams.forEach(teamName => {
      const teamEmployees = employees.filter(emp => emp.team === teamName);
      const teamLeader = teamEmployees.find(emp => emp.position === '팀장');
      const currentLeaderName = teamLeader?.name || '';

      // 팀장 일치 확인 로직 (로깅 최적화)
      let canApplyAdjustment = false;
      if (previousMonthData.employees) {
        const prevTeamEmployees = previousMonthData.employees.filter((emp: any) => emp.team === teamName);
        const prevLeader = prevTeamEmployees.find((emp: any) => emp.position === '팀장');
        canApplyAdjustment = prevLeader && prevLeader.name === currentLeaderName;
      }

      // 지난달 미달성 계산 로직 (로깅 최적화)
      let previousUnderperformance = 0;
      if (canApplyAdjustment && previousMonthData.teamPerformanceData) {
        const prevTeamPerformance = previousMonthData.teamPerformanceData[teamName];
        if (prevTeamPerformance) {
          const targetSales = prevTeamPerformance.targetSales || 0;
          const actualSales = prevTeamPerformance.actualSales || 0;
          const underperformance = targetSales - actualSales;
          previousUnderperformance = Math.max(0, underperformance);
        }
      }

      cache.set(teamName, { canApplyAdjustment, previousUnderperformance });
    });

    return cache;
  }, [applyPreviousMonthAdjustment, previousMonthData, localTeams, employees, selectedMonth]);

  // ═══════════════════════════════════════
  // Calculation Functions
  // ═══════════════════════════════════════

  /**
   * 가중평균 인건비율 계산
   */
  const calculateWeightedAveragePersonnelPercentage = (
    teams: string[],
    employeesList: Employee[],
    month: string,
    budgetDataParam: BudgetData
  ): number => {
    if (!teams || teams.length === 0 || !employeesList || employeesList.length === 0) {
      return 25; // 기본값
    }

    let totalSalary = 0;
    let totalPureTargetSales = 0;

    teams.forEach(teamName => {
      const teamEmployees = employeesList.filter(emp => emp.team === teamName);
      if (teamEmployees.length > 0) {
        const teamSalaryTotal = calculateSalaryTotal(teamEmployees);
        totalSalary += teamSalaryTotal;

        // 팀별 인건비율 가져오기
        const teamPercentage = budgetDataParam.monthlyData?.[month]?.teamPersonnelPercentages?.[teamName] ||
                               budgetDataParam.teamPersonnelPercentages?.[teamName] ||
                               budgetDataParam.monthlyData?.[month]?.personnelPercentage ||
                               budgetDataParam.personnelPercentage || 19;

        const teamPureTarget = calculatePureTargetSales(teamSalaryTotal, teamPercentage);
        totalPureTargetSales += teamPureTarget;
      }
    });

    // 가중평균 인건비율 계산: (총인건비 ÷ 총순수목표매출) × 100
    if (totalPureTargetSales > 0) {
      return (totalSalary / totalPureTargetSales) * 100;
    }

    return 25; // 기본값
  };

  /**
   * 팀별 유효 인건비율 조회 (fallback chain)
   */
  const getEffectivePersonnelPercentage = (teamName: string): number => {
    const currentMonthData = getSafeMonthlyData(budgetData, selectedMonth);

    // UI와 동일한 fallback 로직 사용
    const teamPersonnelPercentage = currentMonthData.teamPersonnelPercentages?.[teamName] ||
                                   budgetData.teamPersonnelPercentages?.[teamName] ||
                                   currentMonthData.personnelPercentage ||
                                   budgetData.personnelPercentage || 0;

    return teamPersonnelPercentage;
  };

  /**
   * 공제액 계산 (4대보험 + 소득세)
   */
  const calculateDeductions = (employee: Employee, includeAllowances: boolean = false) => {
    const { salary, familyCount = 0, nonTaxable = 0, meal=0, housing=0, overtime=0, individual=0, other=0, incentive = 0 } = employee;

    // 🔒 파트(아르바이트)는 공제 없음 (정책 결정 2026-09-04) — 세금 처리를 이 시스템 밖에서 하고
    //    여기에는 나간 비용만 기록한다. 이 분기가 없으면 개별수당 전액이 근로소득으로 잡혀
    //    4대보험·소득세가 붙고, 화면의 '실지급액(수당포함)'이 실제 지급액과 어긋난다.
    if (isPartPosition(employee)) {
      return {
        tax: 0,
        netSalary: individual,
        nationalPension: 0,
        healthInsurance: 0,
        longTermCare: 0,
        employmentInsurance: 0,
        incomeTax: 0,
        localIncomeTax: 0,
        nonTaxableTotal: 0
      };
    }

    const finalNonTaxableAmount = nonTaxable; // 비과세총액 필드 값만 사용

    // 모든 과세소득 포함 여부에 따라 과세대상소득 계산
    const baseSalary = includeAllowances ?
      salary + incentive + (overtime || 0) + (individual || 0) :
      salary;
    const taxableIncome = Math.max(0, baseSalary - finalNonTaxableAmount);
    const adjustedFamilyMembers = familyCount + 1;

    // 과세대상소득 0원(급여 0원 placeholder 직원 등) → 모든 공제 0 (4대보험 최소보험료 미적용)
    // 규칙: taxableIncome <= 0 → 공제 전액 0 / taxableIncome > 0 → 기존 최소하한 유지 (실지급 음수 방지)
    if (taxableIncome <= 0) {
      return {
        tax: 0,
        netSalary: salary,
        nationalPension: 0,
        healthInsurance: 0,
        longTermCare: 0,
        employmentInsurance: 0,
        incomeTax: 0,
        localIncomeTax: 0,
        nonTaxableTotal: finalNonTaxableAmount
      };
    }

    const npBase = Math.min(Math.max(taxableIncome, 390000), 6170000);
    const nationalPension = Math.round(npBase * 0.045 / 10) * 10;

    const hiBase = Math.min(Math.max(taxableIncome, 370000), 119625000);
    const healthInsurance = Math.round(hiBase * 0.03545 / 10) * 10;
    const longTermCare = Math.round(healthInsurance * 0.1281 / 10) * 10;
    const employmentInsurance = Math.round(taxableIncome * 0.009 / 10) * 10;

    const incomeTaxVal = calculateIncomeTax(taxableIncome, adjustedFamilyMembers);
    const localIncomeTax = Math.round(incomeTaxVal * 0.1 / 10) * 10;

    const totalDeductions = nationalPension + healthInsurance + longTermCare + employmentInsurance + incomeTaxVal + localIncomeTax;

    // 실지급액은 기본급 - 세금으로 계산 (인센티브는 별도 지급, 비과세)
    const netSalaryVal = salary - totalDeductions;

      return {
      tax: totalDeductions,
      netSalary: netSalaryVal,
        nationalPension,
        healthInsurance,
        longTermCare,
        employmentInsurance,
      incomeTax: incomeTaxVal,
      localIncomeTax,
      nonTaxableTotal: finalNonTaxableAmount // 실제 적용된 비과세 총액
    };
  };

  /**
   * 팀장/팀원 일치 여부 확인 (캐시 사용)
   */
  const checkTeamAndLeaderMatch = (teamName: string, currentLeaderName: string, previousTeamData: any): boolean => {
    const cacheResult = teamAdjustmentCache.get(teamName);
    if (cacheResult !== undefined) {
      logIfChanged(`leader-match-${teamName}`, { teamName, currentLeaderName, result: cacheResult.canApplyAdjustment }, () => {
      });
      return cacheResult.canApplyAdjustment;
    }

    // 캐시 미스 시 기존 로직 (거의 발생하지 않음)
    if (!previousTeamData || !previousTeamData.employees) return false;

    const prevTeamEmployees = previousTeamData.employees.filter((emp: any) => emp.team === teamName);
    const prevLeader = prevTeamEmployees.find((emp: any) => emp.position === '팀장');
    const isMatch = prevLeader && prevLeader.name === currentLeaderName;

    return isMatch || false;
  };

  /**
   * 지난달 미달성 금액 계산 (캐시 사용)
   */
  const calculatePreviousMonthUnderperformance = (
    teamName: string,
    previousMonthDataParam: any | null
  ): number => {
    // 🚀 캐시 사용으로 성능 최적화
    const cacheResult = teamAdjustmentCache.get(teamName);
    if (cacheResult !== undefined) {
      logIfChanged(`underperformance-${teamName}`, { teamName, result: cacheResult.previousUnderperformance }, () => {
        if (IS_DEVELOPMENT && cacheResult.previousUnderperformance > 0) {
        }
      });
      return cacheResult.previousUnderperformance;
    }

    // 입력값 검증
    if (!previousMonthDataParam?.teamPerformanceData) {
      return 0;
    }

    const prevTeamPerformance = previousMonthDataParam.teamPerformanceData[teamName];
    if (!prevTeamPerformance) {
      return 0;
    }

    // 미달성 금액 계산: max(0, 목표매출 - 실제매출)
    const targetSales = prevTeamPerformance.targetSales || 0;
    const actualSales = prevTeamPerformance.actualSales || 0;
    const underperformance = targetSales - actualSales;

    const result = Math.max(0, underperformance);

    if (IS_DEVELOPMENT && result > 0) {
    }

    return result;
  };

  /**
   * 캐시된 조정 가능성 및 미달성 금액을 한번에 가져오는 최적화 함수
   */
  const getCachedAdjustmentInfo = (teamName: string, currentLeaderName: string): {
    canApplyAdjustment: boolean;
    previousUnderperformance: number;
  } => {
    const cacheResult = teamAdjustmentCache.get(teamName);
    if (cacheResult !== undefined) {
      logIfChanged(`adjustment-info-${teamName}`, {
        teamName,
        currentLeaderName,
        canApply: cacheResult.canApplyAdjustment,
        underperformance: cacheResult.previousUnderperformance
      }, () => {
        if (IS_DEVELOPMENT) {
        }
      });

      return {
        canApplyAdjustment: cacheResult.canApplyAdjustment,
        previousUnderperformance: cacheResult.previousUnderperformance
      };
    }

    // 🐌 캐시 미스 시 개별 함수 호출 (폴백)
    const canApply = checkTeamAndLeaderMatch(teamName, currentLeaderName, previousMonthData);
    const underperformance = canApply ? calculatePreviousMonthUnderperformance(teamName, previousMonthData) : 0;

    return {
      canApplyAdjustment: canApply,
      previousUnderperformance: underperformance
    };
  };


  /**
   * 중앙집중식 최종 목표매출 계산 함수
   */
  const calculateFinalTargetSales = (
    teamName: string,
    pureTargetSales: number,
    teamEmployees: Employee[],
    context: 'auto' | 'manual' | 'bulk' = 'auto'
  ): { finalTargetSales: number; adjustmentApplied: boolean; underperformanceAmount: number } => {
    // 🚫 "미지정" 팀은 조정 대상에서 제외
    if (teamName === '미지정') {
      if (IS_DEVELOPMENT) {
      }
      return { finalTargetSales: pureTargetSales, adjustmentApplied: false, underperformanceAmount: 0 };
    }

    // 🔒 동시 실행 방지 (상호 배제 로직)
    if (context !== 'auto' && executingTeams.has(teamName)) {
      if (IS_DEVELOPMENT) {
      }
      return { finalTargetSales: pureTargetSales, adjustmentApplied: false, underperformanceAmount: 0 };
    }

    // 🔒 팀 실행 상태 추가 (context가 'auto'가 아닌 경우에만)
    if (context !== 'auto') {
      setExecutingTeams(prev => new Set([...prev, teamName]));
    }

    try {
    const currentMonthData = getSafeMonthlyData(budgetData, selectedMonth);

    // ── 정본: 최종목표 = 순수목표 + 저장된 이월금액 ──
    //   이월금액(teamAdjustmentCarryover)이 "이 팀 목표에 이월이 얼마 들어 있나"의 유일한 근거다.
    //   플래그(adjustmentApplied)는 정보용으로만 남긴다 — 운영 DB에서 플래그와 실제 이월이
    //   어긋나 있었기 때문에(실제 이월 6건 전부 false) 플래그로 판정하면 한쪽을 잃는다.
    const storedCarryover = getTeamAdjustmentCarryover(currentMonthData, teamName);
    const isAlreadyAdjusted = storedCarryover > 0 || adjustmentAppliedTeams.has(teamName);

    const teamLeader = teamEmployees.find(emp => emp.position === '팀장');
    const currentLeaderName = teamLeader?.name || '';
    const cachedInfo = getCachedAdjustmentInfo(teamName, currentLeaderName);

    // ── 적용은 명시적 행동('manual'·'bulk')에서만 ──
    //   'auto'(렌더·자동 재계산)는 절대 적용하지 않는다. 이전에는 auto가 메모리에서만 이월을
    //   더하고 기록은 하지 않아, 저장 시 "이월 포함 목표 + 이월 0" 이 함께 영속됐다.
    const canApplyAdjustment = context !== 'auto' &&
                             applyPreviousMonthAdjustment &&
                             previousMonthData &&
                             cachedInfo.canApplyAdjustment &&
                             !isAlreadyAdjusted; // 중복 적용 방지
    const previousUnderperformance = canApplyAdjustment ? cachedInfo.previousUnderperformance : 0;
    const shouldApplyAdjustment = canApplyAdjustment && previousUnderperformance > 0;

    // 적용이면 이번에 동결할 이월금액, 아니면 저장된 이월금액. 호출부는 이 값을 목표와
    // '같은 setBudgetData'에 teamAdjustmentCarryover로 기록해야 한다 (타이머 기록 금지).
    const carryoverToRecord = shouldApplyAdjustment ? previousUnderperformance : storedCarryover;
    const finalTargetSales = pureTargetSales + carryoverToRecord;

    if (shouldApplyAdjustment) {
      // 세션 플래그: 같은 렌더 사이클 내 중복 적용 차단용 (영속 판정은 이월금액이 한다)
      setAdjustmentAppliedTeams(prev => {
        const newSet = new Set(prev);
        newSet.add(teamName);
        return newSet;
      });
    }

    if (IS_DEVELOPMENT) {
      const debugInfo = {
        팀명: teamName,
        호출컨텍스트: context,
        팀장명: currentLeaderName,
        조정활성화여부: applyPreviousMonthAdjustment,
        지난달데이터존재: !!previousMonthData,
        팀장일치여부_캐시: cachedInfo.canApplyAdjustment,
        이미조정적용됨: isAlreadyAdjusted,
        저장된이월금액: storedCarryover.toLocaleString() + '원',
        이번에적용: shouldApplyAdjustment,
        순수목표매출: pureTargetSales.toLocaleString() + '원',
        최종목표매출: finalTargetSales.toLocaleString() + '원',
      };
      void debugInfo;
    }

      return {
        finalTargetSales,
        adjustmentApplied: shouldApplyAdjustment,
        underperformanceAmount: carryoverToRecord
      };
    } finally {
      // 🔒 팀 실행 상태 해제 (상호 배제 해제)
      if (context !== 'auto') {
        setExecutingTeams(prev => {
          const newSet = new Set(prev);
          newSet.delete(teamName);
          return newSet;
        });
      }
    }
  };

  /**
   * 팀별 목표매출 재계산 (모든 팀 동시 재계산)
   */
  const recalculateTeamTargetSales = (teamName: string, latestEmployees?: any[]) => {
    // 🔒 개별 팀 재계산 중복 방지 (빠른 반환)
    if (executingTeams.has(teamName)) {
      return;
    }

    debugGroup(`팀별 목표매출 재계산 시작 - ${teamName}팀`, () => {
      debugLog(`함수 시작`, {
        팀명: teamName,
        선택된월: selectedMonth,
        현재시간: new Date().toLocaleString()
      });

      debugLog(`📊 전체 직원 데이터 상태 확인`, {
        전체직원수: employees.length,
        선택된팀: teamName,
        해당팀직원수: employees.filter(emp => emp.team === teamName).length,
        해당팀직원명단: employees.filter(emp => emp.team === teamName).map(emp => emp.name).join(', ')
      });
    });

    setBudgetData(prev => {
      debugGroup(`${teamName}팀 데이터 업데이트`, () => {
        debugLog(`데이터 계산 시작`);
      });

      const currentMonthData = getSafeMonthlyData(prev, selectedMonth);
      const currentPersonnelPercentage = currentMonthData.personnelPercentage || prev.personnelPercentage || 19;

      debugLog(`현재 월 데이터 확인`, {
        선택된월: selectedMonth,
        전체인건비율: currentPersonnelPercentage,
        기존팀목표매출: currentMonthData.teamTargetSales?.[teamName]?.toLocaleString() || '미설정',
        기존순수목표매출: currentMonthData.teamPureTargetSales?.[teamName]?.toLocaleString() || '미설정'
      });

      // 팀별 인건비 재계산 (최신 상태 확인)
      const currentEmployees = latestEmployees || employees;
      const teamEmployees = currentEmployees.filter(emp => emp.team === teamName);

      debugGroup(`🔍 실제 사용되는 직원 데이터 상세 확인`, () => {
        teamEmployees.forEach(emp => {
          debugLog(`직원 데이터 상세 (${emp.name})`, {
            직원ID: emp.id,
            기본급: emp.salary?.toLocaleString() || '0',
            퇴직적립금: (emp.retirement || 0).toLocaleString(),
            식대: (emp.meal || 0).toLocaleString(),
            주거비: (emp.housing || 0).toLocaleString(),
            초과수당: (emp.overtime || 0).toLocaleString(),
            개인수당: (emp.individual || 0).toLocaleString(),
            기타수당: (emp.other || 0).toLocaleString(),
            '📊_개인총액': (emp.salary + (emp.retirement || 0) + (emp.meal || 0) +
              (emp.housing || 0) + (emp.overtime || 0) + (emp.individual || 0) +
              (emp.other || 0)).toLocaleString() + '원'
          });
        });

        const currentTeamTotal = calculateSalaryTotal(teamEmployees);

        debugLog(`📊 현재 계산되는 팀 인건비 총액`, {
          팀명: teamName,
          직원수: teamEmployees.length,
          계산된총액: currentTeamTotal.toLocaleString() + '원',
          '⚠️_UI표시값과_비교필요': '확인 필요'
        });
      });

      debugGroup(`${teamName}팀 직원 인건비 상세 계산`, () => {
        teamEmployees.forEach(emp => {
          const individualTotal = emp.salary + (emp.retirement || 0) + (emp.meal || 0) +
            (emp.housing || 0) + (emp.overtime || 0) + (emp.individual || 0) + (emp.other || 0);

          debugLog(`직원별 인건비`, {
            직원명: emp.name,
            기본급: emp.salary?.toLocaleString() || 0,
            퇴직적립금: (emp.retirement || 0).toLocaleString(),
            식대: (emp.meal || 0).toLocaleString(),
            주거비: (emp.housing || 0).toLocaleString(),
            초과수당: (emp.overtime || 0).toLocaleString(),
            개인수당: (emp.individual || 0).toLocaleString(),
            기타수당: (emp.other || 0).toLocaleString(),
            개인총액: individualTotal.toLocaleString()
          });
        });
      });

      const teamDirectorAllocation = calculateDirectorCostAllocation(currentEmployees, teamName);
      // 🔒 실장 배분 정본: 실장을 1회만 계상 (teamEmployees에 실장 포함 시 base+배분 이중계상 방지)
      const teamSalaryTotal = calculateTeamSalaryTotal(currentEmployees, teamName, currentEmployees);

      debugCalculationStep(
        '팀 인건비 총액 계산',
        {
          직원수: teamEmployees.length,
          직원명단: teamEmployees.map(emp => emp.name).join(', '),
          실장배분액: teamDirectorAllocation.toLocaleString() + '원'
        },
        teamSalaryTotal.toLocaleString() + '원',
        '각 직원의 (기본급 + 퇴직적립금 + 각종수당) 합계 + 실장 인건비 배분'
      );

      // 팀별 인건비율 가져오기 (공통 함수 사용으로 UI와 로직 통일)
      const teamPersonnelPercentage = getEffectivePersonnelPercentage(teamName);

      debugLog(`팀별 인건비율 확인`, {
        팀명: teamName,
        팀별인건비율: teamPersonnelPercentage + '%',
        전체인건비율: currentPersonnelPercentage + '%',
        '사용되는인건비율': teamPersonnelPercentage + '%'
      });

      // 순수 목표매출 계산
      const teamPureTargetSales = teamPersonnelPercentage > 0
        ? Math.round(teamSalaryTotal / (teamPersonnelPercentage / 100))
        : 0;

      debugCalculationStep(
        '순수목표매출 계산',
        {
          팀인건비총액: teamSalaryTotal.toLocaleString() + '원',
          팀인건비율: teamPersonnelPercentage + '%'
        },
        teamPureTargetSales.toLocaleString() + '원',
        `${teamSalaryTotal.toLocaleString()} ÷ (${teamPersonnelPercentage}% ÷ 100) = ${teamSalaryTotal.toLocaleString()} ÷ ${teamPersonnelPercentage/100}`
      );

      // 🔧 중앙집중식 최종 목표매출 계산 (중복 방지 및 일관성 보장)
      const finalTargetResult = calculateFinalTargetSales(
        teamName,
        teamPureTargetSales,
        teamEmployees,
        'manual' // 수동 재계산 컨텍스트
      );

      const finalTargetSales = finalTargetResult.finalTargetSales;
      const adjustmentApplied = finalTargetResult.adjustmentApplied;
      const previousUnderperformance = finalTargetResult.underperformanceAmount;

      debugGroup(`${teamName}팀 계산 완료 요약`, () => {
        debugLog(`최종 계산 결과`, {
          '💼 인건비 총액': teamSalaryTotal.toLocaleString() + '원',
          '📊 인건비율': teamPersonnelPercentage + '%',
          '🎯 순수목표매출': teamPureTargetSales.toLocaleString() + '원',
          '📈 지난달미달성': previousUnderperformance.toLocaleString() + '원',
          '🏆 최종목표매출': finalTargetSales.toLocaleString() + '원',
          '🔧 조정적용됨': adjustmentApplied,
          '👥 직원수': teamEmployees.length + '명'
        });
      });

      // 🔥 방안 3: 모든 팀의 순수목표매출 재계산
      // 팀 우주 = 이 달의 팀 목록 ∪ 직원이 속한 팀. 직원만 보면 인사이동으로 잠시 0명이 된 팀의
      // 이월·목표 키가 통째 교체(replace)에서 빠져 이월이 영구 유실된다 (Codex 재심 P0-4).
      // 0명 팀은 순수 0, 목표 = 이월 로 남는다. 삭제는 명시적 팀 삭제 동작만 한다.
      const actualTeams = [...new Set([
        ...localTeams,
        ...currentEmployees.map(emp => emp.team),
      ].filter(team => team && String(team).trim() !== ''))];

      debugGroup(`🔥 모든 팀 순수목표매출 재계산`, () => {
        debugLog(`재계산 시작`, {
          전체팀목록: actualTeams.join(', '),
          팀수: actualTeams.length,
          직원데이터기준: '현재 직원들이 속한 팀만 처리'
        });
      });

      const allTeamsPureTargetSales: Record<string, number> = {};
      const allTeamsSalaryTotals: Record<string, number> = {};
      const allTeamsTargetSales: Record<string, number> = {};
      // 이월금액은 목표와 '같은 setBudgetData'에 실린다 — 따로 기록하는 타이머는 두지 않는다
      const allTeamsCarryover: Record<string, number> = {};

      // 모든 팀에 대해 순수목표매출 재계산
      actualTeams.forEach(team => {
        const teamEmps = currentEmployees.filter(emp => emp.team === team);
        // 🔒 실장 배분 정본: 실장을 1회만 계상
        const teamSalaryTotalForTeam = calculateTeamSalaryTotal(currentEmployees, team, currentEmployees);
        const teamPersonnelPercentageForTeam = getEffectivePersonnelPercentage(team);

        // 순수목표매출 계산
        const pureTargetSales = teamPersonnelPercentageForTeam > 0
          ? Math.round(teamSalaryTotalForTeam / (teamPersonnelPercentageForTeam / 100))
          : 0;

        allTeamsPureTargetSales[team] = pureTargetSales;
        allTeamsSalaryTotals[team] = teamSalaryTotalForTeam;

        // 최종목표매출 계산 (현재 팀은 이미 계산했으므로 그 값 사용)
        if (team === teamName) {
          allTeamsTargetSales[team] = finalTargetSales;
          allTeamsCarryover[team] = finalTargetResult.underperformanceAmount; // 새로 동결했거나 저장된 이월
        } else {
          // 타 팀은 정본 그대로: 순수 + 저장된 이월. 한 팀의 재계산 버튼이 다른 팀에 조정을
          // '새로' 적용하지 않는다 — 일괄 적용은 applyAdjustmentToAllEligibleTeams 소관.
          const otherCarryover = getTeamAdjustmentCarryover(currentMonthData, team);
          allTeamsCarryover[team] = otherCarryover;
          allTeamsTargetSales[team] = pureTargetSales + otherCarryover;
        }

        debugLog(`${team}팀 재계산 결과`, {
          팀명: team,
          직원수: teamEmps.length,
          인건비총액: teamSalaryTotalForTeam.toLocaleString() + '원',
          인건비율: teamPersonnelPercentageForTeam + '%',
          순수목표매출: pureTargetSales.toLocaleString() + '원',
          최종목표매출: allTeamsTargetSales[team].toLocaleString() + '원'
        });
      });

      debugGroup(`🔥 모든 팀 재계산 완료`, () => {
        debugLog(`전체 결과 요약`, {
          재계산된팀수: Object.keys(allTeamsPureTargetSales).length,
          총순수목표매출: Object.values(allTeamsPureTargetSales).reduce((sum, val) => sum + val, 0).toLocaleString() + '원',
          총최종목표매출: Object.values(allTeamsTargetSales).reduce((sum, val) => sum + val, 0).toLocaleString() + '원'
        });
      });

      const newTotalTargetSales = Object.values(allTeamsTargetSales).reduce((sum, val) => sum + (Number(val) || 0), 0);

      // 월별 데이터 업데이트 - 완전한 깊은 복사로 다른 월 독립성 보장
      const updatedMonthlyData = JSON.parse(JSON.stringify(prev.monthlyData || {}));
      // 플래그는 정보용으로 이월금액과 동치로 맞춘다 (플래그만 보고 판정하는 곳은 더 이상 없다)
      const syncedFlags: Record<string, boolean> = { ...(currentMonthData.adjustmentApplied || {}) };
      Object.keys(allTeamsCarryover).forEach(t => { syncedFlags[t] = allTeamsCarryover[t] > 0; });

      updatedMonthlyData[selectedMonth] = {
        ...JSON.parse(JSON.stringify(currentMonthData)),
        teamTargetSales: allTeamsTargetSales,  // 모든 팀 최종목표매출 (= 순수 + 이월)
        teamAdjustmentCarryover: allTeamsCarryover,  // 🔒 목표와 같은 업데이트에 기록
        adjustmentApplied: syncedFlags,
        teamPureTargetSales: allTeamsPureTargetSales,  // 🔥 모든 팀 순수목표매출 전체 교체
        teamSalaryTotals: allTeamsSalaryTotals,  // 모든 팀 인건비총액
        targetSales: newTotalTargetSales
      };

      // 현재 월인 경우 전역 데이터도 업데이트
      const currentYearMonth = new Date().toISOString().slice(0, 7);
      if (selectedMonth === currentYearMonth) {
        return {
          ...prev,
          monthlyData: updatedMonthlyData,
          teamTargetSales: allTeamsTargetSales,
          teamPureTargetSales: allTeamsPureTargetSales,
          teamSalaryTotals: allTeamsSalaryTotals,
          targetSales: newTotalTargetSales
        };
      } else {
        return {
          ...prev,
          monthlyData: updatedMonthlyData
        };
      }
    });
  };

  /**
   * 실시간 개인 인센티브 계산
   */
  const calculateRealTimeIndividualIncentive = (employee: Employee): number => {
    const empTeam = employee.team;
    const empPosition = employee.position || employee.role;

    // 🔧 월급(세전) 0원 직원은 인센티브 배분에서 제외
    if (!employee.salary || employee.salary <= 0) {
      return 0;
    }

    // 🔧 팀의 실시간 인센티브산출매출 계산 (allTeamsCalc와 동일 기준 사용)
    // 실장 인건비 배분(calculateDirectorCostAllocation)을 teamPure와 allTeamsCalc 양쪽에
    // '동일하게' 포함한다. 과거 0원 버그의 원인은 배분 자체가 아니라 두 곳의 기준 불일치였으므로,
    // 양쪽을 함께 포함하면 재발하지 않으며 teamTargetSales 정의(#10, 실장 배분 포함)와도 일치한다.
    //
    // 🔒 최종목표 정본 통일: calculateFinalTargetSales(:610-619)와 동일한 조정 보존 시맨틱을 쓴다.
    //   조정 완료(isAlreadyAdjusted) 팀은 순수값 재산출이 아니라 저장된 teamTargetSales(=순수+이월)를
    //   읽어 인센티브 기준목표를 동결 목표와 일치시킨다. 미이식 시 급여 편집으로 순수값이 바뀌면
    //   인센티브 지급 기준이 동결 목표와 (순수_새-순수_옛)만큼 괴리된다.
    // 정본 그대로: 순수 + 저장된 이월. 렌더 경로에서 조정을 '적용'하지 않는다 —
    // 이전에는 여기서 메모리상으로만 이월을 더해, 화면의 인센티브 기준과 저장된 목표가 갈라졌다.
    // (같은 식을 다시 쓰지 않고 정본 함수를 부른다 — 사본이 둘이면 언젠가 갈라진다, Codex 재심 P2-1)
    const resolveTeamFinalTarget = (tn: string, tPure: number): number =>
      resolveFinalTargetSales(budgetData, selectedMonth, tn, tPure);

    const teamEmps = employees.filter(emp => emp.team === empTeam);
    // 🔒 실장 배분 정본: calculateTeamSalaryTotal이 실장을 1회만 계상 (인라인 base+배분은 본인 팀 이중계상)
    const teamSalary = calculateTeamSalaryTotal(employees, empTeam, employees);
    const teamPure = calculatePureTargetSales(teamSalary, getEffectivePersonnelPercentage(empTeam));
    const teamLdr = teamEmps.find(emp => emp.role === '팀장' || emp.position === '팀장');
    const finalTarget = resolveTeamFinalTarget(empTeam, teamPure);
    const actual = teamActualSales[empTeam] || 0;
    const exceeded = Math.max(0, actual - finalTarget);

    // 차감분 계산
    const allTeamsCalc = getSortedTeams(localTeams, teamSortType).map(tn => {
      const tSalary = calculateTeamSalaryTotal(employees, tn, employees);
      const tPure = calculatePureTargetSales(tSalary, getEffectivePersonnelPercentage(tn));
      // 🔒 최종목표 정본: 순수 + 저장된 이월 (위 resolveTeamFinalTarget과 동일)
      const tFinalTarget = resolveTeamFinalTarget(tn, tPure);
      const tActual = teamActualSales[tn] || 0;
      const tExceeded = Math.max(0, tActual - tFinalTarget);
      const tUnderperformed = Math.max(0, tFinalTarget - tActual);
      return { teamName: tn, exceeded: tExceeded, underperformed: tUnderperformed };
    });

    const totalExceeded = allTeamsCalc.reduce((sum, t) => sum + t.exceeded, 0);
    const totalUnderperformed = allTeamsCalc.reduce((sum, t) => sum + t.underperformed, 0);
    const totalDeduction = totalUnderperformed * (underperformanceDeductionRate / 100);

    let deduction = 0;
    if (exceeded > 0 && totalExceeded > 0 && totalDeduction > 0) {
      // 1로 clamp: empTeam이 allTeamsCalc(localTeams) 집합에 아직 없는 1프레임 transient에서
      // exceeded가 totalExceeded보다 커져 비율이 1을 초과하면 차감이 초과분을 넘어 인센티브가
      // 잘못 0원이 될 수 있음. 정상 상태에선 항상 ≤1이라 영향 없음.
      const deductionRatio = Math.min(1, exceeded / totalExceeded);
      deduction = Math.round(totalDeduction * deductionRatio);
    }

    const incentiveSales = Math.max(0, exceeded - deduction);

    // 인센티브 비율 가져오기
    const teamIncentiveSettings = resolveTeamIncentiveRatio(budgetData, selectedMonth, empTeam);

    // 팀 전체 인센티브 계산
    const teamPerformanceIncentive = incentiveSales * (teamIncentiveSettings.performance / 100);

    // 실장 선공제: 실장이 존재하면 실장 몫을 먼저 차감
    const director = employees.find(emp => emp.position === '실장' && emp.directorIncentiveRate);
    const directorRate = director?.directorIncentiveRate || 0;
    const directorDeduction = teamPerformanceIncentive * (directorRate / 100);
    const adjustedTeamIncentive = teamPerformanceIncentive - directorDeduction;

    // 실장의 인센티브 = 모든 팀의 선공제 합산 (allTeamsCalc 재활용)
    if (empPosition === '실장' && directorRate > 0) {
      let totalDirectorIncentive = 0;
      allTeamsCalc.forEach(t => {
        let tDeduction = 0;
        if (t.exceeded > 0 && totalExceeded > 0 && totalDeduction > 0) {
          tDeduction = Math.round(totalDeduction * (t.exceeded / totalExceeded));
        }
        const tIncentiveSales = Math.max(0, t.exceeded - tDeduction);
        const tSettings = resolveTeamIncentiveRatio(budgetData, selectedMonth, t.teamName);
        const tPerfIncentive = tIncentiveSales * (tSettings.performance / 100);
        totalDirectorIncentive += tPerfIncentive * (directorRate / 100);
      });
      return Math.round(totalDirectorIncentive);
    }

    // 🚫 파트 직위는 인센티브 배분 대상 아님 (팀원 분모에서도 제외되므로 받는 쪽도 제외해야 과분배 방지)
    if (empPosition === '파트') {
      return 0;
    }

    // 개인 인센티브 계산
    let realTimeIncentive = 0;
    if (empPosition === '팀장') {
      realTimeIncentive = adjustedTeamIncentive * (teamIncentiveSettings.team / 100);
    } else {
      const teamMembers = teamEmps.filter(e => (e.position === '팀원' || (!e.position && e.role !== '팀장')) && e.salary > 0).length;
      if (teamMembers > 0) {
        const teamMemberTotalIncentive = adjustedTeamIncentive * (teamIncentiveSettings.member / 100);
        realTimeIncentive = teamMemberTotalIncentive / teamMembers;
      }
    }

    return Math.round(realTimeIncentive);
  };

  /**
   * 실지급액(세후) 계산 - 모든 과세소득을 포함한 세금 계산
   */
  const calculateNetSalaryWithAllowances = (employee: Employee): number => {
    const deductionsWithAllowances = calculateDeductions(employee, true);
    return employee.salary + calculateRealTimeIndividualIncentive(employee) + (employee.overtime || 0) + (employee.individual || 0) - deductionsWithAllowances.tax;
  };

  /**
   * 미달성팀 차감 비율 업데이트
   */
  const updateUnderperformanceDeductionRate = (value: string | number) => {
    const numValue = typeof value === 'string' ? Number(value) : value;
    const clampedValue = Math.max(0, Math.min(100, numValue)); // 0-100% 범위 제한

    setUnderperformanceDeductionRate(clampedValue);

    // 즉시 budgetData 업데이트
    setBudgetData(prev => {
      // 기존 월 데이터를 모두 보존한 뒤 차감비율만 덮어쓴다.
      // (고정 필드 목록으로 재구성하면 teamPureTargetSales·teamPerformanceData·
      //  adjustmentApplied 등 다른 경로가 저장한 필드가 유실됨)
      const currentMonthData = getSafeMonthlyData(prev, selectedMonth);
      return {
        ...prev,
        underperformanceDeductionRate: clampedValue,
        monthlyData: {
          ...prev.monthlyData,
          [selectedMonth]: {
            ...currentMonthData,
            underperformanceDeductionRate: clampedValue
          }
        }
      };
    });

    // 명시저장 전환: 즉시 저장하지 않고 in-memory 반영만 한다(위 setBudgetData).
    // dirty 표시만 하고, 실제 Firebase 저장은 '저장' 버튼(internalSaveAllData)이 수행한다.
    setIsDataChanged(true);
    addChangeLog(`미달성팀 차감 비율: ${clampedValue}% (변경됨 · 미저장)`);
  };

  // ═══════════════════════════════════════
  // Return
  // ═══════════════════════════════════════

  return {
    // Memoized values
    teamAdjustmentCache,

    // Pure calculation functions (also exported at module level)
    calculateIncomeTax,
    calculateIncentiveCalculationSales,

    // Hook calculation functions
    calculateWeightedAveragePersonnelPercentage,
    getEffectivePersonnelPercentage,
    calculateDeductions,
    calculateNetSalaryWithAllowances,
    calculateRealTimeIndividualIncentive,
    recalculateTeamTargetSales,
    calculateFinalTargetSales,
    checkTeamAndLeaderMatch,
    calculatePreviousMonthUnderperformance,
    getCachedAdjustmentInfo,
    updateUnderperformanceDeductionRate,
    logIfChanged,
  };
}
